/**
 * Cart Drawer Enhancements
 * – Dynamic ship-by date (today + 3 business days Mon–Fri)
 * – Upsell carousel via Shopify Recommendations API
 */

function calcShipDate() {
  const d = new Date();
  let biz = 0;
  while (biz < 3) {
    d.setDate(d.getDate() + 1);
    const day = d.getDay();
    if (day !== 0 && day !== 6) biz++;
  }
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function updateShipDates() {
  const date = calcShipDate();
  document.querySelectorAll('.cdp-shipdate').forEach(el => { el.textContent = date; });
}

function formatMoney(cents) {
  const dollars = cents / 100;
  return '$' + (Number.isInteger(dollars) ? dollars.toFixed(0) : dollars.toFixed(2));
}

let upsellTimer = null;

async function loadUpsells() {
  const wrap = document.querySelector('.cart-drawer__upsells');
  if (!wrap) return;

  const productId = wrap.dataset.firstProduct;
  if (!productId) { wrap.hidden = true; return; }

  const inCartIds = (wrap.dataset.cartProductIds || '').split(',').map(Number).filter(Boolean);

  try {
    const resp = await fetch(`/recommendations/products.json?product_id=${productId}&limit=6&intent=related`);
    if (!resp.ok) throw new Error('failed');
    const { products } = await resp.json();

    const upsells = products.filter(p => !inCartIds.includes(p.id)).slice(0, 3);
    if (!upsells.length) { wrap.hidden = true; return; }

    const track = wrap.querySelector('.cdu-track');
    if (!track) return;

    track.innerHTML = upsells.map(p => {
      const v = p.variants[0];
      const oneVariant = p.variants.length === 1 && v.available;
      const isOnSale = v.compare_at_price && v.compare_at_price > v.price;
      const imgUrl = p.featured_image
        ? p.featured_image.replace(/(\.(jpe?g|png|gif|webp))(\?.*)?$/i, '_160x160$1')
        : '';
      const safeTitle = p.title.replace(/"/g, '&quot;');
      return `<div class="cdu-card">
        <a href="${p.url}" class="cdu-img-link" tabindex="-1" aria-hidden="true">
          ${imgUrl
            ? `<img src="${imgUrl}" alt="" loading="lazy" class="cdu-img" width="160" height="160">`
            : `<div class="cdu-img cdu-img--placeholder"></div>`}
        </a>
        <div class="cdu-info">
          <a href="${p.url}" class="cdu-name">${p.title}</a>
          <div class="cdu-prices"><span class="cdu-price${isOnSale ? ' cdu-price--sale' : ''}">${formatMoney(v.price)}</span>${isOnSale ? `<s class="cdu-compare-price">${formatMoney(v.compare_at_price)}</s>` : ''}</div>
        </div>
        <button class="cdu-add button"
                type="button"
                data-variant="${v.id}"
                data-one="${oneVariant}"
                aria-label="${oneVariant ? 'Add' : 'View'} ${safeTitle}">
          ${oneVariant ? 'Add' : 'View'}
        </button>
      </div>`;
    }).join('');

    track.querySelectorAll('.cdu-add').forEach(btn => {
      btn.addEventListener('click', async () => {
        if (btn.dataset.one !== 'true') {
          const card = btn.closest('.cdu-card');
          const link = card?.querySelector('.cdu-name');
          if (link) {
            sessionStorage.removeItem('theme-drawer-open');
            window.location.href = link.href;
          }
          return;
        }
        btn.disabled = true;
        const orig = btn.textContent;
        btn.textContent = '···';
        try {
          const r = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ id: Number(btn.dataset.variant), quantity: 1 })
          });
          const data = await r.json();
          if (!r.ok) throw new Error(data.description || 'Error');
          const cart = await fetch('/cart.js').then(res => res.json());

          // Legacy Horizon (cart:update)
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: { resource: data, data: { itemCount: cart.item_count } }
          }));
          // New Horizon (shopify:cart:lines-update) — cart-icon reads event.promise
          const linesEvent = new CustomEvent('shopify:cart:lines-update', { bubbles: true });
          linesEvent.promise = Promise.resolve({ cart: { totalQuantity: cart.item_count } });
          document.dispatchEvent(linesEvent);
          btn.textContent = '✓';
          setTimeout(() => { btn.disabled = false; btn.textContent = orig; }, 2500);
        } catch {
          btn.disabled = false;
          btn.textContent = orig;
        }
      });
    });

    wrap.removeAttribute('hidden');
  } catch {
    wrap.hidden = true;
  }
}

function scheduleUpsells() {
  clearTimeout(upsellTimer);
  upsellTimer = setTimeout(loadUpsells, 350);
}

// Watch for dialog open/close
const dialogObserver = new MutationObserver(mutations => {
  for (const m of mutations) {
    if (m.type === 'attributes' && m.attributeName === 'open') {
      updateShipDates();
      scheduleUpsells();
    }
  }
});

// Watch for SRA re-renders (childList mutations inside the drawer) and
// re-populate ship date if any .cdp-shipdate elements came back blank.
let shipDateRepairTimer = null;
function onDrawerMutation() {
  clearTimeout(shipDateRepairTimer);
  shipDateRepairTimer = setTimeout(() => {
    const blank = Array.from(document.querySelectorAll('#cart-drawer .cdp-shipdate'))
      .some(el => !el.textContent.trim());
    if (blank) updateShipDates();
  }, 80);
}

function startCartDrawerObserver() {
  const drawer = document.querySelector('#cart-drawer');
  if (!drawer) return;
  new MutationObserver(onDrawerMutation).observe(drawer, { subtree: true, childList: true });
}

function init() {
  updateShipDates();
  // HH uses theme-drawer with id="cart-drawer"; Harryheather-Halifax used .cart-drawer__dialog
  const dialog = document.querySelector('#cart-drawer dialog');
  if (dialog) {
    dialogObserver.observe(dialog, { attributes: true });
    if (dialog.open) scheduleUpsells();
  }
  startCartDrawerObserver();
}

// Re-run after cart updates (both legacy and HH event names)
document.addEventListener('cart:update', () => {
  setTimeout(() => { updateShipDates(); scheduleUpsells(); }, 500);
});
document.addEventListener('shopify:cart:lines-update', () => {
  setTimeout(() => { updateShipDates(); scheduleUpsells(); }, 600);
});
window.addEventListener('pagehide', () => {
  sessionStorage.removeItem('theme-drawer-open');
});
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
